/**
 * MeshImport.ts — a 3D file of any format three.js reads (and IFC) to the plain
 * triangle groups the bridge model keeps as a custom mesh.
 * -----------------------------------------------------------------------------
 * The host (Modeler ▸ Custom Meshes ▸ Import, or an agent through MCP) hands the
 * file's bytes and name to `parseMesh`; what comes back is one group per colour —
 * positions and triangle indices, base64 — and nothing of three.js. The model
 * stores that and draws it through the ordinary scene payload, so the file is
 * read once, here, and never again.
 *
 * Coordinates come back AS THE FILE HAS THEM, in its own units and with its own
 * up axis: those are properties of the mesh in the model (Source Units, Up Axis)
 * that the engineer can still correct after the import. `units` and `upAxis` in
 * the result are only this format's usual convention — glTF is metres and Y-up by
 * its specification, STL/3MF/AMF are millimetre CAD formats with Z up, and so on.
 *
 * IFC goes through web-ifc (WebAssembly), loaded on first use from lib/web-ifc —
 * it is far too large to ride in this bundle for the files that never need it.
 * Draco-compressed glTF takes its decoder from lib/draco the same way.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader.js';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { ColladaLoader } from 'three/examples/jsm/loaders/ColladaLoader.js';
import { ThreeMFLoader } from 'three/examples/jsm/loaders/3MFLoader.js';
import { TDSLoader } from 'three/examples/jsm/loaders/TDSLoader.js';
import { AMFLoader } from 'three/examples/jsm/loaders/AMFLoader.js';
import { USDZLoader } from 'three/examples/jsm/loaders/USDZLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import type * as WebIFCTypes from 'web-ifc';

/** File extensions parseMesh reads, for the host's file picker and its error message. */
export const MESH_FORMATS = ['ifc', 'glb', 'gltf', 'obj', 'stl', 'ply', 'fbx', 'dae', '3mf', '3ds', 'amf', 'usdz', 'json'];

export interface ParsedMeshGroup {
  /** "#rrggbb". */
  color: string;
  /** 0–1. */
  opacity: number;
  /** Float32 x,y,z per vertex, little-endian, base64. */
  positions: string;
  /** Uint32 vertex indices, three per triangle, little-endian, base64. */
  indices: string;
}

export interface ParsedMesh {
  format: string;
  /** The format's usual length unit: 'm' | 'mm' | 'cm' | 'ft' | 'in'. */
  units: string;
  /** The format's usual up axis: 'Y' | 'Z'. */
  upAxis: string;
  triangles: number;
  vertices: number;
  /** [minX, minY, minZ, maxX, maxY, maxZ] in the file's own frame. */
  bounds: number[];
  groups: ParsedMeshGroup[];
  /** Anything worth telling the engineer: a skipped part, a texture-only colour. */
  notes: string[];
}

/** Each format's convention, as far as it has one. */
const CONVENTION: Record<string, { units: string; upAxis: string }> = {
  ifc: { units: 'm', upAxis: 'Y' },    // web-ifc hands back metres, Y-up, whatever the file says
  glb: { units: 'm', upAxis: 'Y' },
  gltf: { units: 'm', upAxis: 'Y' },
  usdz: { units: 'm', upAxis: 'Y' },
  json: { units: 'm', upAxis: 'Y' },
  dae: { units: 'm', upAxis: 'Y' },    // ColladaLoader applies <unit> and turns Z_UP files Y-up itself
  obj: { units: 'm', upAxis: 'Y' },
  fbx: { units: 'cm', upAxis: 'Y' },
  stl: { units: 'mm', upAxis: 'Z' },
  '3mf': { units: 'mm', upAxis: 'Z' },
  amf: { units: 'mm', upAxis: 'Z' },
  '3ds': { units: 'm', upAxis: 'Z' },
  ply: { units: 'm', upAxis: 'Z' },
};

const FALLBACK_COLOR = '#b4b4b4';

/**
 * Read a 3D file into colour groups. `data` is the file's bytes (Blazor passes a
 * byte[] as a Uint8Array); `fileName` decides the format by its extension.
 */
export async function parseMesh(fileName: string, data: Uint8Array | ArrayBuffer): Promise<ParsedMesh> {
  const bytes = data instanceof Uint8Array ? data : new Uint8Array(data);
  const ext = (fileName.split('.').pop() ?? '').toLowerCase();
  const conv = CONVENTION[ext];
  if (!conv) throw new Error(`'${fileName}' is not a format that can be imported. Use one of: ${MESH_FORMATS.join(', ')}.`);

  const notes: string[] = [];
  const root = ext === 'ifc' ? await readIfc(bytes, notes) : await readWithThree(ext, bytes, notes);
  const out = flatten(root, notes);
  disposeTree(root);

  if (out.triangles === 0) throw new Error(`'${fileName}' holds no triangle geometry that could be imported.`);
  return { format: ext, units: conv.units, upAxis: conv.upAxis, ...out, notes };
}

// ---------------------------------------------------------------------------------------------
//  three.js loaders
// ---------------------------------------------------------------------------------------------

/** Library files (draco decoder, web-ifc) sit beside the page, under lib/. */
function libUrl(path: string): string {
  return new URL(`lib/${path}`, document.baseURI).href;
}

/**
 * A manager that answers every external reference — a texture beside the file, an
 * .mtl, a .bin — with nothing. Only the bytes handed in exist here; without this
 * each reference would be a request to the page's own origin that can only 404.
 * Embedded data (data:, blob:) and our own library files load normally.
 */
function isolatedManager(): THREE.LoadingManager {
  const manager = new THREE.LoadingManager();
  manager.setURLModifier((url) => {
    if (/^(data|blob):/i.test(url) || url.startsWith(libUrl(''))) return url;
    return 'data:application/octet-stream;base64,';
  });
  return manager;
}

function text(bytes: Uint8Array): string {
  return new TextDecoder().decode(bytes);
}

function buffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

async function readWithThree(ext: string, bytes: Uint8Array, notes: string[]): Promise<THREE.Object3D> {
  const manager = isolatedManager();
  const meshOf = (g: THREE.BufferGeometry) => {
    // STL / PLY carry colour per vertex when they carry it at all.
    const mat = new THREE.MeshStandardMaterial({ color: FALLBACK_COLOR, vertexColors: !!g.getAttribute('color') });
    return new THREE.Mesh(g, mat);
  };

  switch (ext) {
    case 'glb':
    case 'gltf': {
      const loader = new GLTFLoader(manager);
      const draco = new DRACOLoader(manager);
      draco.setDecoderPath(libUrl('draco/'));
      loader.setDRACOLoader(draco);
      loader.setMeshoptDecoder(MeshoptDecoder);
      try {
        const gltf = await new Promise<{ scene: THREE.Group }>((resolve, reject) =>
          loader.parse(ext === 'glb' ? buffer(bytes) : text(bytes), '', resolve as never, reject));
        return gltf.scene;
      } finally {
        draco.dispose();
      }
    }
    case 'obj': {
      // Without its .mtl (only the one file is here) every material is the loader's default white.
      const obj = new OBJLoader(manager).parse(text(bytes));
      obj.traverse((o) => {
        const mats = (o as THREE.Mesh).material;
        for (const mat of Array.isArray(mats) ? mats : mats ? [mats] : []) (mat as THREE.MeshPhongMaterial).color?.set(FALLBACK_COLOR);
      });
      return obj;
    }
    case 'stl':
      return meshOf(new STLLoader(manager).parse(buffer(bytes)));
    case 'ply':
      return meshOf(new PLYLoader(manager).parse(buffer(bytes)));
    case 'fbx':
      return new FBXLoader(manager).parse(buffer(bytes), '');
    case 'dae': {
      const dae = new ColladaLoader(manager).parse(text(bytes), '');
      if (!dae?.scene) throw new Error('The Collada file holds no scene.');
      return dae.scene;
    }
    case '3mf':
      return new ThreeMFLoader(manager).parse(buffer(bytes));
    case '3ds':
      return new TDSLoader(manager).parse(buffer(bytes), '');
    case 'amf':
      return new AMFLoader(manager).parse(buffer(bytes));
    case 'usdz':
      notes.push('USDZ is read through three.js, which takes only the text (usda) layers of the package.');
      return new USDZLoader(manager).parse(buffer(bytes));
    case 'json':
      return new THREE.ObjectLoader(manager).parse(JSON.parse(text(bytes)));
  }
  throw new Error(`No reader for '.${ext}'.`);
}

// ---------------------------------------------------------------------------------------------
//  IFC through web-ifc
// ---------------------------------------------------------------------------------------------

type WebIfcModule = typeof WebIFCTypes;
let ifcApi: Promise<WebIFCTypes.IfcAPI> | null = null;

/** web-ifc's IIFE build, attached as window.WebIFC by a script tag on first use. */
function loadWebIfc(): Promise<WebIfcModule> {
  const w = window as unknown as { WebIFC?: WebIfcModule };
  if (w.WebIFC) return Promise.resolve(w.WebIFC);
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = libUrl('web-ifc/web-ifc-api-iife.js');
    s.onload = () => (w.WebIFC ? resolve(w.WebIFC) : reject(new Error('web-ifc did not load.')));
    s.onerror = () => reject(new Error('The IFC reader (lib/web-ifc) could not be loaded.'));
    document.head.appendChild(s);
  });
}

function webIfc(): Promise<WebIFCTypes.IfcAPI> {
  ifcApi ??= (async () => {
    const mod = await loadWebIfc();
    const api = new mod.IfcAPI();
    // Single-threaded: the threaded build needs cross-origin isolation the WebView does not give.
    await api.Init((path: string) => (path.endsWith('.wasm') ? libUrl('web-ifc/web-ifc.wasm') : path), true);
    api.SetLogLevel(mod.LogLevel.LOG_LEVEL_OFF);
    return api;
  })().catch((e) => { ifcApi = null; throw e; });
  return ifcApi;
}

/** IFC classes left out of an import: bars and tendons are most of a detailed bridge's
 *  triangles (a swept tube along every bar) and nothing a context mesh needs; openings
 *  and spaces are voids, not things. */
const IFC_SKIPPED = ['IFCREINFORCINGBAR', 'IFCREINFORCINGMESH', 'IFCTENDON', 'IFCTENDONANCHOR', 'IFCOPENINGELEMENT', 'IFCSPACE'];

/**
 * Every IFC element's body as meshes, in web-ifc's frame: metres, Y-up, the
 * project moved near the origin (COORDINATE_TO_ORIGIN) — a survey-coordinate
 * site would otherwise lose its millimetres to float precision. Placing it is
 * the engineer's job afterwards, like any other custom mesh.
 */
async function readIfc(bytes: Uint8Array, notes: string[]): Promise<THREE.Object3D> {
  const mod = await loadWebIfc();
  const ifc = await webIfc();
  const mid = ifc.OpenModel(bytes, { COORDINATE_TO_ORIGIN: true, CIRCLE_SEGMENTS: 12 });
  if (mid < 0 || !ifc.IsModelOpen(mid)) throw new Error('web-ifc could not read this IFC file.');

  const codes = mod as unknown as Record<string, number | undefined>;
  const skippedTypes = new Set(IFC_SKIPPED.map((n) => codes[n]).filter((t): t is number => typeof t === 'number'));
  let skipped = 0;
  let triangles = 0;

  const root = new THREE.Group();
  const materials = new Map<string, THREE.MeshBasicMaterial>();
  try {
    ifc.StreamAllMeshes(mid, (mesh) => {
      if (skippedTypes.has(ifc.GetLineType(mid, mesh.expressID))) { skipped++; return; }
      const geoms = mesh.geometries;
      for (let g = 0; g < geoms.size(); g++) {
        const pg = geoms.get(g);
        const geometry = ifc.GetGeometry(mid, pg.geometryExpressID);
        try {
          const n = geometry.GetIndexDataSize();
          if (n < 3) continue;
          triangles += n / 3;
          if (triangles > MAX_TRIANGLES) throw tooLarge(triangles, true);

          const v = ifc.GetVertexArray(geometry.GetVertexData(), geometry.GetVertexDataSize());
          const idx = ifc.GetIndexArray(geometry.GetIndexData(), n);

          // x y z nx ny nz per vertex: keep the positions only.
          const pos = new Float32Array((v.length / 6) * 3);
          for (let i = 0, j = 0; i < v.length; i += 6, j += 3) {
            pos[j] = v[i]; pos[j + 1] = v[i + 1]; pos[j + 2] = v[i + 2];
          }
          const bg = new THREE.BufferGeometry();
          bg.setAttribute('position', new THREE.BufferAttribute(pos, 3));
          bg.setIndex(new THREE.BufferAttribute(new Uint32Array(idx), 1));

          const c = pg.color;
          const key = `${c.x.toFixed(3)}|${c.y.toFixed(3)}|${c.z.toFixed(3)}|${c.w.toFixed(3)}`;
          let mat = materials.get(key);
          if (!mat) {
            mat = new THREE.MeshBasicMaterial({ transparent: c.w < 1, opacity: Math.max(0.05, Math.min(1, c.w)) });
            // An element with no style comes back pure white: the neutral grey reads better.
            if (c.x > 0.999 && c.y > 0.999 && c.z > 0.999) mat.color.set(FALLBACK_COLOR);
            else mat.color.setRGB(c.x, c.y, c.z, THREE.SRGBColorSpace);
            materials.set(key, mat);
          }
          const m = new THREE.Mesh(bg, mat);
          m.matrixAutoUpdate = false;
          m.matrix.fromArray(pg.flatTransformation);
          root.add(m);
        } finally {
          geometry.delete();
        }
      }
    });
  } catch (e) {
    disposeTree(root);
    throw e;
  } finally {
    ifc.CloseModel(mid);
  }
  if (skipped > 0) notes.push(`${skipped.toLocaleString()} reinforcing bars, tendons, openings and spaces were left out.`);
  return root;
}

// ---------------------------------------------------------------------------------------------
//  Flatten to colour groups
// ---------------------------------------------------------------------------------------------

/**
 * The most triangles one custom mesh may have. Every triangle of it travels in
 * the scene payload with the bridge on each redraw, as text: a million is about
 * 80 MB of it, already more than a detailed bridge with all its reinforcement.
 */
export const MAX_TRIANGLES = 1_000_000;

function tooLarge(triangles: number, streaming = false): Error {
  return new Error(`The file has more than ${MAX_TRIANGLES.toLocaleString()} triangles `
    + `(${Math.round(triangles).toLocaleString()}${streaming ? ' and counting' : ''}), more than a custom mesh can carry. `
    + 'Export it at a lower level of detail, or only the part that is needed.');
}

/** A typed array that grows as it is appended to. */
class Grow<T extends Float32Array | Uint32Array> {
  length = 0;
  constructor(public data: T) {}
  push3(a: number, b: number, c: number): void {
    if (this.length + 3 > this.data.length) {
      const next = new (this.data.constructor as { new (n: number): T })(Math.max(1024, this.data.length * 2));
      next.set(this.data);
      this.data = next;
    }
    this.data[this.length++] = a;
    this.data[this.length++] = b;
    this.data[this.length++] = c;
  }
  view(): T { return this.data.subarray(0, this.length) as T; }
}

interface Bucket {
  color: string;
  opacity: number;
  pos: Grow<Float32Array>;
  idx: Grow<Uint32Array>;
}

/** Triangles `root` will flatten to, instances counted — to refuse a file before building it. */
function countTriangles(root: THREE.Object3D): number {
  let n = 0;
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;
    const g = mesh.geometry as THREE.BufferGeometry;
    const count = g.getIndex()?.count ?? g.getAttribute('position')?.count ?? 0;
    n += (count / 3) * ((o as THREE.InstancedMesh).isInstancedMesh ? (o as THREE.InstancedMesh).count : 1);
  });
  return n;
}

/**
 * Every mesh under `root`, in root's frame, into one bucket per colour and
 * opacity. Vertices a mesh shares between its triangles stay shared; nothing is
 * welded across meshes. Instanced meshes are expanded, mirrored transforms have
 * their winding turned back, points and lines are left out.
 */
function flatten(root: THREE.Object3D, notes: string[]): Omit<ParsedMesh, 'format' | 'units' | 'upAxis' | 'notes'> {
  const total = countTriangles(root);
  if (total > MAX_TRIANGLES) throw tooLarge(total);

  root.updateMatrixWorld(true);
  const rootInv = root.matrixWorld.clone().invert();

  const buckets = new Map<string, Bucket>();
  const box = new THREE.Box3();
  const v = new THREE.Vector3();
  const m = new THREE.Matrix4();
  const inst = new THREE.Matrix4();
  let triangles = 0;
  let vertices = 0;
  let textured = false;
  let skippedLines = false;

  const bucketFor = (color: string, opacity: number): Bucket => {
    const key = `${color}|${opacity.toFixed(3)}`;
    let b = buckets.get(key);
    if (!b) buckets.set(key, (b = { color, opacity, pos: new Grow(new Float32Array(3072)), idx: new Grow(new Uint32Array(3072)) }));
    return b;
  };

  root.traverse((o) => {
    const obj = o as THREE.Mesh;
    if ((o as THREE.Line).isLine || (o as THREE.Points).isPoints) { skippedLines = true; return; }
    if (!obj.isMesh || !obj.geometry) return;

    const geom = obj.geometry as THREE.BufferGeometry;
    const position = geom.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (!position || position.count === 0) return;

    const index = geom.getIndex();
    const triCount = index ? index.count : position.count;
    const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
    const groups = geom.groups.length
      ? geom.groups
      : [{ start: 0, count: triCount, materialIndex: 0 }];

    const instanced = (o as THREE.InstancedMesh).isInstancedMesh ? (o as THREE.InstancedMesh) : null;
    const copies = instanced ? instanced.count : 1;
    // This mesh's vertex → the bucket's, so shared vertices stay shared. -1: not placed yet.
    const map = new Int32Array(position.count);

    for (let c = 0; c < copies; c++) {
      m.multiplyMatrices(rootInv, obj.matrixWorld);
      if (instanced) { instanced.getMatrixAt(c, inst); m.multiply(inst); }
      const flip = m.determinant() < 0;

      for (const grp of groups) {
        const mat = materials[grp.materialIndex ?? 0] ?? materials[0];
        const { color, opacity, fromTexture } = colourOf(mat, geom, index, grp.start, grp.count);
        if (fromTexture) textured = true;
        const b = bucketFor(color, opacity);
        map.fill(-1);

        const at = (vi: number): number => {
          let k = map[vi];
          if (k < 0) {
            v.fromBufferAttribute(position, vi).applyMatrix4(m);
            k = b.pos.length / 3;
            b.pos.push3(v.x, v.y, v.z);
            box.expandByPoint(v);
            map[vi] = k;
            vertices++;
          }
          return k;
        };

        const end = Math.min(grp.start + grp.count, triCount);
        for (let i = grp.start; i + 2 < end; i += 3) {
          const a = index ? index.getX(i) : i;
          const bb = index ? index.getX(i + 1) : i + 1;
          const cc = index ? index.getX(i + 2) : i + 2;
          if (a === bb || bb === cc || a === cc) continue;
          const ka = at(a), kb = at(bb), kc = at(cc);
          if (flip) b.idx.push3(ka, kc, kb); else b.idx.push3(ka, kb, kc);
          triangles++;
        }
      }
    }
  });

  if (textured) notes.push('Some surfaces are coloured by an image texture; they take the material\'s base colour (or a neutral grey) instead.');
  if (skippedLines) notes.push('Lines and points in the file were left out: only surfaces are imported.');

  const groups: ParsedMeshGroup[] = [];
  for (const b of buckets.values()) {
    if (b.idx.length === 0) continue;
    groups.push({
      color: b.color,
      opacity: b.opacity,
      positions: toBase64(b.pos.view()),
      indices: toBase64(b.idx.view()),
    });
  }

  const bounds = box.isEmpty() ? [0, 0, 0, 0, 0, 0] : [box.min.x, box.min.y, box.min.z, box.max.x, box.max.y, box.max.z];
  return { triangles, vertices, bounds, groups };
}

/**
 * The flat colour a material draws a group in. A material coloured per vertex
 * takes the average of the group's vertex colours; one coloured only by a map
 * keeps its base colour, which a texture usually leaves white — read as grey.
 */
function colourOf(mat: THREE.Material | undefined, geom: THREE.BufferGeometry, index: THREE.BufferAttribute | null,
                  start: number, count: number): { color: string; opacity: number; fromTexture: boolean } {
  const any = mat as THREE.MeshStandardMaterial | undefined;
  const opacity = any && any.transparent ? Math.max(0.05, Math.min(1, any.opacity ?? 1)) : 1;
  const colors = geom.getAttribute('color') as THREE.BufferAttribute | undefined;

  if (colors && (any?.vertexColors ?? false)) {
    let r = 0, g = 0, b = 0, n = 0;
    const end = Math.min(start + count, index ? index.count : colors.count);
    const step = Math.max(1, Math.floor((end - start) / 4096));   // a sample is plenty for an average
    for (let i = start; i < end; i += step) {
      const vi = index ? index.getX(i) : i;
      r += colors.getX(vi); g += colors.getY(vi); b += colors.getZ(vi); n++;
    }
    if (n > 0) {
      const c = new THREE.Color(r / n, g / n, b / n);
      return { color: '#' + c.getHexString(THREE.SRGBColorSpace), opacity, fromTexture: false };
    }
  }

  const base = any?.color as THREE.Color | undefined;
  if (!base) return { color: FALLBACK_COLOR, opacity, fromTexture: false };

  const hasMap = !!any?.map;
  const hex = '#' + base.getHexString(THREE.SRGBColorSpace);
  if (hasMap && hex.toLowerCase() === '#ffffff') return { color: FALLBACK_COLOR, opacity, fromTexture: true };
  return { color: hex, opacity, fromTexture: hasMap };
}

function toBase64(a: Float32Array | Uint32Array): string {
  // Typed arrays are little-endian on every platform this runs on, which is what the host reads.
  const u8 = new Uint8Array(a.buffer, a.byteOffset, a.byteLength);
  let s = '';
  const chunk = 0x8000;
  for (let i = 0; i < u8.length; i += chunk) {
    s += String.fromCharCode.apply(null, u8.subarray(i, i + chunk) as unknown as number[]);
  }
  return btoa(s);
}

function disposeTree(root: THREE.Object3D): void {
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    mesh.geometry?.dispose();
    const mats = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
    for (const mat of mats) {
      for (const value of Object.values(mat)) if ((value as THREE.Texture)?.isTexture) (value as THREE.Texture).dispose();
      mat.dispose();
    }
  });
}
